import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { AuthService } from '../../../core/services/auth.service';
import { PasswordValidators } from '../../../core/validators/password.validator';
import { CommonModule } from '@angular/common';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzInputGroupComponent, NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import {
  UserOutline,
  EyeInvisibleOutline,
} from '@ant-design/icons-angular/icons';
@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzFormControlComponent,
    NzInputGroupComponent,
    NzFormItemComponent,
    NzButtonModule,
    NzFormLabelComponent,
    CommonModule,
    NzInputModule,
    NzMessageModule,
    SvgIconComponent,
    NzIconModule,
  ],
  styleUrl: './reset-password.component.scss',
  templateUrl: `./reset-password.component.html`,
})
export class ResetPasswordComponent implements OnDestroy, OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly message = inject(NzMessageService);
  private readonly destroy$ = new Subject<void>();
  private nzIconService = inject(NzIconService);
  passwordVisible = false;
  passwordConfirmVisible = false;
  isLoading = false;
  errorMessage = '';
  token = '';
  isTokenValid = false;

  ngOnInit(): void {
    this.nzIconService.addIcon(UserOutline, EyeInvisibleOutline);
    this.route.queryParams.subscribe(params => {
      if (params['token']) {
        this.token = params['token'];
        this.authService.validateResetToken(this.token).subscribe({
          next: () => {
            this.isTokenValid = true;
            // this.message.success('User Validated Successfully.');
          },
          error: error => {
            console.error(error);
            this.isTokenValid = false;
            this.errorMessage = 'User Validation Failed.';
            setTimeout(() => {
              this.errorMessage = '';
              this.router.navigate(['/login']);
            }, 3000);
          },
          complete: () => {
            this.isLoading = false;
          },
        });
      }
    });
  }

  resetPasswordForm: FormGroup = this.fb.group(
    {
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          PasswordValidators.createValidator,
        ],
      ],
      confirmPassword: ['', Validators.required],
    },
    { validators: PasswordValidators.matchPassword }
  );

  onSubmit(): void {
    if (this.resetPasswordForm.valid && this.token) {
      this.isLoading = true;
      const { newPassword, confirmPassword } = this.resetPasswordForm.value;

      this.authService
        .resetPassword(this.token, newPassword, confirmPassword)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.message.success(
              'Password reset successful. Please login with your new password.'
            );
            this.resetPasswordForm.reset();
          },
          error: error => {
            this.isLoading = false;
            this.errorMessage = error.message;
            setTimeout(() => {
              this.errorMessage = '';
            }, 3000);
          },
          complete: () => {
            this.isLoading = false;
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
